// covered-by: frontends/carrie/src/services/__tests__/askPuntt.test.ts
// covered-by: qa/rickrubin/tests/api/ask-puntt-share.spec.ts
import {
  type AskPunttChatScope,
  type GetAskPunttChatHistoryResponse,
  type ListAskPunttChatsResponse,
} from "@mg/schemas/src/christo/askPuntt";
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useUI } from "../../contexts/ui";
import * as AskPunttService from "../../services/askPuntt";

/**
 * React Query hooks for Ask Puntt chat API
 */

// Query keys for cache management. The `chatsPrefix` / `allChatsPrefix`
// keys are intended for broad invalidations (`invalidateQueries({ queryKey:
// askPunttKeys.chatsPrefix })` invalidates "mine", "shared", and "all"
// scopes together because TanStack Query does prefix matching).
export const askPunttKeys = {
  all: ["ask-puntt"] as const,
  chatsPrefix: ["ask-puntt", "chats"] as const,
  allChatsPrefix: ["ask-puntt", "all-chats"] as const,
  chats: (scope?: AskPunttChatScope) =>
    [...askPunttKeys.all, "chats", scope ?? "mine"] as const,
  allChats: (scope?: AskPunttChatScope) =>
    [...askPunttKeys.all, "all-chats", scope ?? "mine"] as const,
  chat: (chatId: string) => [...askPunttKeys.all, "chat", chatId] as const,
  messages: (chatId: string) =>
    [...askPunttKeys.chat(chatId), "messages"] as const,
  streaming: (chatId: string, messageId: string) =>
    [...askPunttKeys.chat(chatId), "streaming", messageId] as const,
  userSearch: (q: string) => [...askPunttKeys.all, "user-search", q] as const,
} as const;

/**
 * List recent Ask Puntt chats for sidebar.
 *
 * `scope` defaults to "mine" (chats owned by the requester). Pass "shared"
 * for the "Shared with me" sidebar group, or "all" for a unified list with
 * `role: "owner" | "shared"` per row.
 */
export function useAskPunttChats(options?: {
  enabled?: boolean;
  scope?: AskPunttChatScope;
}) {
  const scope = options?.scope ?? "mine";
  return useQuery({
    queryKey: askPunttKeys.chats(scope),
    queryFn: () => AskPunttService.listChats({ limit: 20, scope }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * List all Ask Puntt chats (owned + shared-with-me; MG admins see enterprise).
 */
export function useAskPunttAllChats(options?: {
  enabled?: boolean;
  scope?: AskPunttChatScope;
}) {
  const scope = options?.scope ?? "all";
  return useQuery({
    queryKey: askPunttKeys.allChats(scope),
    queryFn: () => AskPunttService.listChats({ limit: 200, scope }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Get full chat history for a specific chat.
 * Pass refetchInterval to enable polling (e.g. while waiting for an AI response).
 *
 * `refetchInterval` accepts either a static number/false or a function that
 * receives the current Query so the caller can branch on the latest fetched
 * data (e.g. continue polling while the chat document is in the limbo window
 * between `reviewChecklist` being saved and `reviewStatus` being set).
 */
export function useAskPunttChatHistory(
  chatId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<GetAskPunttChatHistoryResponse, Error>,
        ) => number | false | undefined);
  },
) {
  return useQuery({
    queryKey: askPunttKeys.messages(chatId || ""),
    queryFn: () => AskPunttService.getChatHistory(chatId!),
    enabled: !!chatId && options?.enabled !== false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Create a new Ask Puntt chat (sends first message and gets response)
 */
export function useCreateAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: AskPunttService.createChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to create Ask Puntt chat:", error);
      notify({
        title: "Failed to create chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Delete an Ask Puntt chat and invalidate the chats list.
 */
export function useDeleteAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (chatId: string) => AskPunttService.deleteChat(chatId),
    onSuccess: (_data, chatId) => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
      queryClient.removeQueries({ queryKey: askPunttKeys.chat(chatId) });
    },
    onError: (error) => {
      console.error("Failed to delete Ask Puntt chat:", error);
      notify({
        title: "Failed to delete chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Rename an Ask Puntt chat (owner-only). Optimistically updates the chat title
 * in BOTH the chat-history cache (drives the breadcrumb at the top of the chat
 * view) AND every cached chats list (drives the sidebar + chats page), then
 * invalidates so the server's canonical title wins. Reverts every touched
 * cache and toasts on error.
 */
export function useRenameAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      AskPunttService.renameChat(chatId, title),
    onMutate: async ({ chatId, title }) => {
      const trimmed = title.trim();
      const historyKey = askPunttKeys.messages(chatId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: historyKey }),
        queryClient.cancelQueries({ queryKey: askPunttKeys.chatsPrefix }),
        queryClient.cancelQueries({ queryKey: askPunttKeys.allChatsPrefix }),
      ]);

      const previousHistory =
        queryClient.getQueryData<GetAskPunttChatHistoryResponse>(historyKey);
      const previousLists =
        queryClient.getQueriesData<ListAskPunttChatsResponse>({
          queryKey: askPunttKeys.all,
        });

      queryClient.setQueryData<GetAskPunttChatHistoryResponse>(
        historyKey,
        (old) =>
          old ? { ...old, chat: { ...old.chat, title: trimmed } } : old,
      );

      // Update every cached chats list (mine / shared / all scopes) so the
      // sidebar row and chats page reflect the new title immediately.
      for (const [key, value] of previousLists) {
        if (!value?.chats) continue;
        queryClient.setQueryData<ListAskPunttChatsResponse>(key, {
          ...value,
          chats: value.chats.map((c) =>
            c._id === chatId ? { ...c, title: trimmed } : c,
          ),
        });
      }

      return { previousHistory, previousLists, historyKey };
    },
    onError: (error, _vars, context) => {
      if (context?.previousHistory !== undefined) {
        queryClient.setQueryData(context.historyKey, context.previousHistory);
      }
      for (const [key, value] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, value);
      }
      console.error("Failed to rename Ask Puntt chat:", error);
      notify({
        title: "Failed to rename chat",
        message: "Your change was reverted. Please try again.",
        variant: "error",
      });
    },
    onSettled: (_data, _error, { chatId }) => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
  });
}

/**
 * Lightweight polling for a single message's status fields.
 * Returns only streaming-relevant fields via MongoDB projection.
 */
export function useAskPunttMessageStatus(
  chatId: string | null,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: [...askPunttKeys.chat(chatId || ""), "message-status", messageId],
    queryFn: () => AskPunttService.getMessageStatus(chatId!, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Submit thumbs up/down feedback on an assistant message.
 * Optimistically updates the message in the query cache.
 */
export function useSubmitAskPunttFeedback(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      messageId: string;
      feedback: "thumbs_up" | "thumbs_down";
    }) =>
      AskPunttService.submitFeedback(chatId, params.messageId, params.feedback),
    onMutate: async (params) => {
      const queryKey = askPunttKeys.messages(chatId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === params.messageId
                ? { ...m, feedback: params.feedback }
                : m,
            ),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          askPunttKeys.messages(chatId),
          context.previous,
        );
      }
    },
  });
}

/**
 * Send a message to an existing Ask Puntt chat.
 * Returns the saved user message and a workflowId (AI response arrives async).
 */
export function useSendAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (payload: {
      message: string;
      attachments?: {
        key: string;
        source: string;
        type: string;
        name?: string;
      }[];
      sectionContext?: { id: string; title: string };
      mentions?: string[];
      mentionEmails?: string[];
    }) => AskPunttService.sendMessage(chatId, payload),
    onSuccess: (_data, _variables, _context) => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.chat(chatId),
      });
    },
    onError: (error) => {
      console.error("Failed to send Ask Puntt message:", error);
      notify({
        title: "Failed to send message",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Abort an in-progress Ask Puntt message (stop generating).
 * Optimistically clears waitingForAI state; caller handles revert on error.
 */
export function useAbortAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) =>
      AskPunttService.abortMessage(chatId, messageId),
    onSuccess: (data) => {
      // Apply the final (partial) message to the cache so polling stops naturally
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === data.message._id
                ? { ...m, ...data.message, status: "completed" as const }
                : m,
            ),
          };
        },
      );
    },
  });
}

/**
 * Lightweight streaming state poll for a single message.
 * Returns only the fields needed during streaming (no full chat document).
 * Uses 500ms polling interval for faster streaming updates.
 */
export function useAskPunttStreamingState(
  chatId: string,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttKeys.streaming(chatId, messageId || ""),
    queryFn: () => AskPunttService.getMessageStreamingState(chatId, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Share an Ask Puntt chat with one or more enterprise users (owner-only).
 * On success invalidates the chat history cache so the share dialog and
 * header attribution re-render with the updated allowList, and the
 * "Shared with me" listings on recipients' sessions stay current after a
 * 30s poll.
 */
export function useShareAskPunttChat(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (payload: { userIds?: string[]; emails?: string[] }) =>
      AskPunttService.shareChat(chatId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to share Ask Puntt chat:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Remove a user from an Ask Puntt chat's allowList (owner-only).
 */
export function useUnshareAskPunttChat(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (userId: string) => AskPunttService.unshareChat(chatId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to unshare Ask Puntt chat:", error);
      notify({
        title: "Failed to remove user",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Server-side debounced enterprise user search. Caller controls the
 * debounce by gating `q` (e.g. via a 250ms debounced state value).
 */
export function useSearchEnterpriseUsers(
  q: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const trimmed = (q ?? "").trim();
  return useQuery({
    queryKey: askPunttKeys.userSearch(trimmed),
    queryFn: () =>
      AskPunttService.searchEnterpriseUsers(trimmed, options?.limit),
    enabled: options?.enabled !== false,
    // Bare `@` loads the roster — refetch whenever the menu reopens.
    staleTime: trimmed.length === 0 ? 0 : 30 * 1000,
    gcTime: 60 * 1000,
  });
}
